import type { EvalRetryError } from "@tsmono/inspect-common";

/**
 * Derive the exception class name (e.g. `RuntimeError`) from a Python
 * traceback's final line. `EvalRetryError` has no dedicated field for this.
 * Returns null when the final line doesn't look like `ExceptionClass[: message]`
 * so callers can omit the chip rather than render garbage.
 */
export function deriveErrorType(retry: EvalRetryError): string | null {
  const tb = retry.traceback.trimEnd();
  if (!tb) return null;
  const lines = tb.split("\n");
  const last = lines[lines.length - 1]?.trim() ?? "";
  if (!last) return null;

  const beforeColon = last.split(":")[0]?.trim() ?? "";
  if (!beforeColon) return null;

  // Keep the final dotted segment: `asyncio.exceptions.TimeoutError` -> `TimeoutError`.
  const cls = beforeColon.split(".").pop() ?? beforeColon;

  // A real exception class is a bare identifier. Anything with whitespace or
  // punctuation is free-form traceback text, not an exception name.
  return /^[A-Za-z_]\w*$/.test(cls) ? cls : null;
}

/**
 * Best-effort attempt start time — the earliest event timestamp. `EvalRetryError`
 * carries no top-level timing, so the events are the only source; never
 * fabricate one. Returns null when no event carries a parseable timestamp.
 */
export function attemptStartTime(retry: EvalRetryError): Date | null {
  const events = retry.events;
  if (!events || events.length === 0) return null;

  const times = events
    .map((e) => ("timestamp" in e ? e.timestamp : undefined))
    .filter((t): t is string => typeof t === "string")
    .map((t) => Date.parse(t))
    .filter((n) => Number.isFinite(n));

  if (times.length === 0) return null;
  return new Date(Math.min(...times));
}
